import type { Ref } from 'vue'
import type { CoursesResponse } from '~~/shared/types/course'

/**
 * Lädt die nächsten Kurstermine eines Kurstyps über `/api/courses`.
 *
 * Läuft bewusst serverseitig (statt `server: false`), damit die Termine im
 * ausgelieferten HTML stehen und nicht erst client-seitig nachgeladen werden –
 * sonst sind sie für AI-Crawler unsichtbar, obwohl die Daten existieren.
 * Die betroffenen Routen sind in `nuxt.config.ts` per `routeRules` vom
 * statischen Prerendering ausgenommen (kurze SWR-Cachezeit statt Rebuild-Pflicht).
 */
export function useLiveCourses(slug: Ref<string | null | undefined> | string, key: string) {
  const slugRef = typeof slug === 'string' ? ref(slug) : slug

  const { data, status } = useAsyncData(
    key,
    () => slugRef.value
      ? $fetch<CoursesResponse>('/api/courses', { query: { slug: slugRef.value } })
      : Promise.resolve({ courses: [] }),
    {
      watch: [slugRef],
      default: () => ({ courses: [] })
    }
  )

  const courses = computed(() => data.value?.courses ?? [])
  const isLoading = computed(() => status.value === 'pending' || status.value === 'idle')

  return { courses, status, isLoading }
}
